"use client"

import * as React from "react"
import { ChevronsUpDown, Shield, Building2, Check, Loader2 } from "lucide-react"
import { collection, doc, getDoc, getDocs } from "firebase/firestore"

import { cn } from "@/lib/utils"
import { useAuth } from "@/contexts/AuthContext"
import { auth, db } from "@/config/firebase"
import type { Organization } from "@/types/auth"
import { PLAN_CONFIG } from "@/components/settings/OrgSettingsPanel"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Badge } from "@/components/ui/Badge"
import { API_URL } from "@/config/env"

export function TeamSwitcher() {
  const { isMobile } = useSidebar()
  const {
    isSystemAdmin,
    isSuperAdmin,
    systemRole,
    userProfile,
    userRole,
    organizations,
    activeOrganization,
    activeOrganizationId,
    isViewingAsOrg,
    switchOrganization,
    stopViewingAsOrg,
    viewAsOrganization,
  } = useAuth()

  const [switching, setSwitching] = React.useState<string | null>(null)
  const [switchError, setSwitchError] = React.useState<string | null>(null)

  const handleSwitch = async (orgId: string) => {
    setSwitching(orgId)
    setSwitchError(null)
    try {
      await switchOrganization(orgId)
    } catch (err: any) {
      console.error('[TeamSwitcher] switchOrganization failed:', err)
      setSwitchError(err?.message || 'Failed to switch organization')
    } finally {
      setSwitching(null)
    }
  }

  // State for all organizations (super admin only)
  const [allOrganizations, setAllOrganizations] = React.useState<Organization[]>([])
  const [loadingOrgs, setLoadingOrgs] = React.useState(false)

  // Real names for legacy orgs that appear in userProfile.organizations array but not
  // in the users/{uid}/organizations subcollection (these default to org ID as name)
  const [legacyOrgNames, setLegacyOrgNames] = React.useState<Record<string, string>>({})

  // Load all organizations for super admin
  React.useEffect(() => {
    if (isSuperAdmin) {
      const loadAllOrgs = async () => {
        setLoadingOrgs(true)
        try {
          const orgsRef = collection(db, "organizations")
          const snapshot = await getDocs(orgsRef)
          const orgs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Organization[]
          setAllOrganizations(orgs.sort((a, b) => a.name.localeCompare(b.name)))
        } catch (error) {
          console.error("Error loading organizations:", error)
        } finally {
          setLoadingOrgs(false)
        }
      }
      loadAllOrgs()
    }
  }, [isSuperAdmin])

  // Determine what to display
  const isAdminWithNoOrg = isSystemAdmin && !activeOrganization

  // Merge subcollection orgs with userProfile.organizations array field.
  // The subcollection is authoritative for names/roles, but the profile array is the source
  // of truth for which orgs the user belongs to (stale subcollections may be missing entries).
  const profileOrgs: Array<{ organizationId: string; role: string }> =
    (userProfile as any)?.organizations ?? []

  // Load real org names for legacy orgs (those in userProfile.organizations array
  // but not yet in the users/{uid}/organizations subcollection)
  React.useEffect(() => {
    const legacyOrgsNeedingNames = profileOrgs.filter(
      po => !organizations.find(o => o.organizationId === po.organizationId)
    )
    if (legacyOrgsNeedingNames.length === 0) return

    const loadLegacyNames = async () => {
      // Repair member docs first — without them, Firestore rules block org reads
      try {
        const idToken = await auth.currentUser?.getIdToken()
        if (idToken) {
          const API_BASE = API_URL
          await fetch(`${API_BASE}/organizations/repair-memberships`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
          })
        }
      } catch {
        // Non-fatal — proceed and let individual reads fail gracefully
      }

      const names: Record<string, string> = {}
      await Promise.all(
        legacyOrgsNeedingNames.map(async (po) => {
          try {
            const orgDoc = await getDoc(doc(db, 'organizations', po.organizationId))
            names[po.organizationId] = orgDoc.exists()
              ? (orgDoc.data()?.name || po.organizationId)
              : po.organizationId
          } catch {
            names[po.organizationId] = po.organizationId
          }
        })
      )
      setLegacyOrgNames(prev => ({ ...prev, ...names }))
    }
    loadLegacyNames()
  }, [profileOrgs, organizations])

  const switchableOrgs = React.useMemo(() => {
    const merged = [...organizations]
    for (const po of profileOrgs) {
      if (!merged.find(o => o.organizationId === po.organizationId)) {
        merged.push({
          organizationId: po.organizationId,
          role: po.role as any,
          organizationName: legacyOrgNames[po.organizationId] || po.organizationId,
          organizationSlug: '',
          organizationLogoURL: null,
          joinedAt: null as any,
          isFavorite: false,
        })
      }
    }
    return merged
  }, [organizations, profileOrgs, legacyOrgNames])

  const hasMultipleOrgs = switchableOrgs.length > 1
  // Super admins can always switch to view any organization
  const canSwitch = isSuperAdmin || hasMultipleOrgs || isViewingAsOrg

  // Get user's display name from profile - always show the user's name
  const userName = userProfile?.displayName || userProfile?.email?.split('@')[0] || 'User'
  const displayName = userName
  const displayInitials = userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2)

  // Show org/role info below the name
  const roleLabel = userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : null
  const displayPlan = isViewingAsOrg
    ? `Viewing: ${activeOrganization?.name || 'Org'}`
    : isAdminWithNoOrg
      ? (systemRole === 'super_admin' ? 'Super Admin' : 'Support')
      : activeOrganization
        ? (roleLabel ? `${activeOrganization.name} · ${roleLabel}` : activeOrganization.name)
        : 'No Organization'

  // If can't switch (regular user with 1 org), show without dropdown
  if (!canSwitch) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            className="h-auto data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div className="flex flex-col w-full gap-1">
              <div className="flex items-center gap-2">
                <div className={cn(
                  "flex aspect-square size-8 items-center justify-center rounded-lg shrink-0",
                  isAdminWithNoOrg ? "bg-accent-red" : "bg-sidebar-primary"
                )}>
                  <span className={cn(
                    "font-semibold text-xs",
                    isAdminWithNoOrg ? "text-white" : "text-sidebar-primary-foreground"
                  )}>
                    {displayInitials}
                  </span>
                </div>
                <span className="truncate font-semibold text-sm">{displayName}</span>
              </div>
              <Badge
                variant={isViewingAsOrg || isAdminWithNoOrg ? "destructive" : "secondary"}
                className="text-xs w-fit"
              >
                {displayPlan}
              </Badge>
              {!isAdminWithNoOrg && !isViewingAsOrg && activeOrganization?.subscription?.plan && PLAN_CONFIG[activeOrganization.subscription.plan] && (
                <Badge className={cn("text-xs w-fit", PLAN_CONFIG[activeOrganization.subscription.plan].className)}>
                  {PLAN_CONFIG[activeOrganization.subscription.plan].label}
                </Badge>
              )}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  // Render dropdown for switching or exiting view-as mode
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="h-auto data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex flex-col w-full gap-1">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "flex aspect-square size-8 items-center justify-center rounded-lg shrink-0",
                    isAdminWithNoOrg ? "bg-accent-red" : "bg-sidebar-primary"
                  )}>
                    <span className={cn(
                      "font-semibold text-xs",
                      isAdminWithNoOrg ? "text-white" : "text-sidebar-primary-foreground"
                    )}>
                      {displayInitials}
                    </span>
                  </div>
                  <span className="truncate font-semibold text-sm flex-1">{displayName}</span>
                  <ChevronsUpDown className="size-4 shrink-0" />
                </div>
                <Badge
                  variant={isViewingAsOrg || isAdminWithNoOrg ? "destructive" : "secondary"}
                  className="text-xs w-fit"
                >
                  {displayPlan}
                </Badge>
                {!isAdminWithNoOrg && !isViewingAsOrg && activeOrganization?.subscription?.plan && PLAN_CONFIG[activeOrganization.subscription.plan] && (
                  <Badge className={cn("text-xs w-fit", PLAN_CONFIG[activeOrganization.subscription.plan].className)}>
                    {PLAN_CONFIG[activeOrganization.subscription.plan].label}
                  </Badge>
                )}
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg max-h-80 overflow-y-auto"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            {isViewingAsOrg ? (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Viewing as Organization
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={stopViewingAsOrg}
                  className="gap-2 p-2 cursor-pointer"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border bg-accent-red">
                    <Shield className="size-3.5 shrink-0 text-white" />
                  </div>
                  <span className="font-medium">Exit View As Mode</span>
                </DropdownMenuItem>
              </>
            ) : isSuperAdmin ? (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  View as Organization
                </DropdownMenuLabel>
                {loadingOrgs ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  </div>
                ) : allOrganizations.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">
                    No organizations found
                  </div>
                ) : (
                  allOrganizations.map((org) => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => viewAsOrganization(org.id, "Organization switcher")}
                      className="gap-2 p-2 cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border bg-sidebar-primary">
                        <Building2 className="size-3.5 shrink-0 text-sidebar-primary-foreground" />
                      </div>
                      <span className="flex-1 truncate">{org.name}</span>
                      {activeOrganizationId === org.id && (
                        <Check className="size-4 text-accent-blue" />
                      )}
                    </DropdownMenuItem>
                  ))
                )}
              </>
            ) : (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Switch Organization
                </DropdownMenuLabel>
                {switchableOrgs.map((org) => (
                  <DropdownMenuItem
                    key={org.organizationId}
                    onClick={() => handleSwitch(org.organizationId)}
                    disabled={switching !== null}
                    className="gap-2 p-2 cursor-pointer"
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border bg-sidebar-primary shrink-0">
                      {switching === org.organizationId
                        ? <Loader2 className="size-3.5 animate-spin text-sidebar-primary-foreground" />
                        : <Building2 className="size-3.5 shrink-0 text-sidebar-primary-foreground" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block truncate">{org.organizationName || org.organizationId}</span>
                      {org.role && (
                        <span className="block text-xs text-muted-foreground capitalize">{org.role}</span>
                      )}
                    </div>
                    {activeOrganizationId === org.organizationId && switching === null && (
                      <Check className="size-4 text-accent-blue shrink-0" />
                    )}
                  </DropdownMenuItem>
                ))}
                {switchError && (
                  <div className="px-2 py-1.5 text-xs text-status-error">{switchError}</div>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
